import { GameWorld } from "../GameWorld";
import { MapRow } from "../Game Map/MapRow";
import { TileBase } from "../Ground Tiles/TileBase";
import { ePlatformState, Platform } from "../Platforms/Platform";
import { View } from "../View/View";
import { TIMER, Gameplay, eBatchGroup } from "../../Gameplay";
import { Game, eModelAsset, eSfxSprite, SfxSprites, eAudioAsset, eAtlas_0, eTextureAsset, eLogEvent, eGameAPI } from "../../../Game";
import { Controls } from "./Controls";
import { ViewCellMap } from "../View/ViewCellMap";
import { Rectangle } from "../../../Utils/2D Shapes/Rectangle";
import { ViewCellMapCell } from "../View/ViewCellMapCell";
import { Score } from "../../../Utils/Score";
import { eCellType, eTileType } from "../Game Map/MapCell";
import { TextureAtlasUtils } from "../../../Utils/TextuAtlasUtils";
import { PathGenerator } from "../Game Map/PathGenerator";
import { Checkpoint } from "../../Checkpoint";
import { SoundManager } from "../../../Utils/SoundManager";
import * as Gamee from "../../../SDK/Gamee/GameeWrapper";
import { eMisstionType } from "../../Gamee Missions/MissionType";
import { eHUDState, HUD } from "../HUD/HUD";
import { SaveState } from "../../../SaveState";



export const enum ePlayerState {
    /**
     * Ceka se na zahajeni hry
     */
    idle,
    /**
     * Kulicka se kutali po hernim svete
     */
    rolling,
    /**
     * Skok
     */
    jump,
    /**
     * Kulicka jede na pojizdne plosine
     */
    rideOnPlatform,
    /**
     * Kulicka vypadla z cesty
     */
    fallToDeath,
    /**
     * Kulicka narazila do prekazky
     */
    crashToDeath,
    /**
     * Hrac je mrtvy
     */
    death,
}



export class Player {
    /**
     * (RO) Staticky pristup
     */
    static get instance(): Player { return Player._instance; }
    private static _instance: Player;

    get entity(): pc.Entity { return this._entity; }
    private _entity: pc.Entity;

    private _shadow: pc.Entity;

    /**
     * (RO) Aktualni stav hrac
     */
    get state(): ePlayerState { return this._state; }
    private _state: ePlayerState;

    private _stateProcessFnc: (() => void)[];
    private _stateTime: number;

    //#region Udalosti

    /**
     * Udalost vyvolana po smrti hrace
     * Emitter: Gameplay.events
     * Parameters: player: Player
     */
    static readonly EVENT_DEATH = "pl:death";

    //#region 

    //#region Pozice

    /**
     * Minimalni pozice na ose X
     */
    static readonly MIN_X = 0;

    /**
     * Maximalni pozice na ose X
     */
    static readonly MAX_X = MapRow.WIDTH * TileBase.SIZE;

    /**
     * Offset hrace od zeme
     */
    static readonly GROUND_OFFSET_Y = 0.375;

    /**
     * (RO) Pozice hrace
     */
    get position(): pc.Vec3 { return this._pos; }
    private _pos: pc.Vec3;

    /**
     * (RO) Pozice originu pro vypocet pozice kamery
     */
    get cameraOrigin(): pc.Vec3 { return this._cameraOrigin; }
    private _cameraOrigin: pc.Vec3;

    /**
     * Aktualni rotace
     */
    private _rotation: pc.Vec3;

    //#endregion

    //#region Pohyb

    /**
     * Zakladni rychlost pohybu hrace smerem vpred v jednotkach za vterinu
     */
    private static readonly DEF_V_SPEED = 4.5;

    /**
     * Maximalni horizontalni rychlost v jednotkach za vterinu
     */
    private static readonly MAX_H_SPEED = 7;

    /**
     * Zrychleni horizontalni rychlost v jednotkach za vterinu
     */
    private static readonly H_SPEED_ACCELERATION = 14;

    /**
     * Samovolne snizeni horizontalni rychlosti, pokud na kulicku nepusobi zadna sila
     */
    private static readonly H_SPEED_DECELERATION = 7;

    /**
     * Aktualni horizontalni rychlost v metrech za vterinu
     */
    private _hSpeed: number;

    /**
     * Aktualni rychlost na ose Z v metrech ze vterinu
     */
    private _vSpeed: number;

    /**
     * Rychlost pohybu na ose Z v metrech za vterinu
     */
    get rollingSpeed(): number { return this._vSpeed; }

    /**
     * Scale pro rychlost rolovani. Rychlost pohybu hrace po ose Z = _rollingSpeed * _rollingSpeedScale.
     */
    private _vSpeedScale: number;

    /**
     * Plosina, na ktere se hrac veze nebo undefined
     */
    private _platform: Platform;

    /**
     * Kolizni obdelnik plosiny
     */
    private _platformColRc: Rectangle;

    //#endregion

    //#region Skok

    /**
     * Vyska, kterou hrac dosahne pri skoku
     */
    private static readonly JUMP_HEIGHT = 2.5;

    /** 
     * Pozice hrace na ose Z pri zahajeni skoku
     */
    private _jumpStartZ: number;

    /**
     * Vzdalenost, kterou hrac skokem prekona na ose Z
     */
    private _jumpDistance: number;

    //#endregion

    //#region Pad

    /**
     * Pocatecni rychlost padu v jednotkach za vterinu
     */
    private static readonly FALL_START_SPEED = 8;

    /**
     * Zvyseni rychlosti padu v jednotakch za 1 vterinu
     */
    private static readonly FALL_SPEED_INC = 4;

    /**
     * Aktualni rychlost padu v jednotkach za vterinu
     */
    private _fallSpeed: number;

    //#endregion

    //#region Kolize

    private static readonly GROUND_COLRC_DEF_SIZE = 0.35;

    /**
     * Polomer kolizni koule
     */
    static readonly COL_RADIUS = 0.38;

    /**
     * Obdelnik pro kolizi se zemi
     */
    private _groundColRc: Rectangle;

    /**
     * Pole pro bunky mapy view, se kterymi ma hrac kolizi
     */
    private _groundColCells: ViewCellMapCell[];

    /**
     * BoundingSphere pro kolizi s prekazkami
     */
    get colSphere(): pc.BoundingSphere { return this._colSphere; }
    private _colSphere: pc.BoundingSphere;

    /**
     * (RO) True, pokud se maji testovat kolize
     */
    get checkCollisions(): boolean { return this._state == ePlayerState.rolling || this._state == ePlayerState.jump; }

    //#endregion

    //#region Skore

    /**
     * (RO) Spravce skore hrace
     */
    get score(): Score { return this._score; }
    private _score: Score;

    /**
     * (RO) True, pokud hrac prekonal pri posledni smrti sve nejvyssi skore
     */
    get isNewHighScore(): boolean { return this._isNewHighScore; }
    private _isNewHighScore: boolean;

    /**
     * Hodnota navysovani skore za vzdalenost
     */
    private static readonly DIS_SCORE = 5;

    /**
     * Vzdalenost, po ktere se skore navysuje
     */
    private static readonly DIS_SCORE_INTERVAL = 5;

    /**
     * Pozice hrace na ose Z, pri ktere doslo k poslednimu navyseni skore za ujetou vzdalenost
     */
    private _disScorePos: number;

    //#endregion

    //#region  Checkpoint

    /**
     * (RO) Checkpoint
     */
    get checkpoint(): Checkpoint { return this._checkpoint; }
    private _checkpoint: Checkpoint;

    /**
     * Pozice pristiho checkpointu v hernim svete
     */
    private _nextCheckPointPos: number;

    /**
     * (RO) Zbyvajici pocet zivotu
     */
    get lifeCnt(): number { return this._lifeCnt; }
    private _lifeCnt: number;

    //#endregion

    /**
     * Ovladani
     */
    private _controls: Controls;

    /**
     * Particle system pro explozi hrace
     */
    private _crashPartSys: pc.ParticleSystemComponent;

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    constructor() {
        Player._instance = this;

        // Funkce pro zpracovani jednotlivych stavu
        this._stateProcessFnc = [];
        this._stateProcessFnc[ePlayerState.idle] = this.processIdle;
        this._stateProcessFnc[ePlayerState.rolling] = this.processRolling;
        this._stateProcessFnc[ePlayerState.jump] = this.processJump;
        this._stateProcessFnc[ePlayerState.fallToDeath] = this.processFall;
        this._stateProcessFnc[ePlayerState.crashToDeath] = this.processCrash;

        // Entita
        this._entity = new pc.Entity();
        this._entity.addComponent(pc.eCompomentType.model, {
            type: pc.eModelType.asset,
            asset: Game.app.assets.find(eModelAsset.player, pc.ASSET_MODEL),
            castShadows: false,
            castShadowsLightmap: false,
            receiveShadows: false,
            lightmapped: false,
        });
        this._entity.setLocalScale(1.4, 1.4, 1.4);
        Game.app.root.addChild(this._entity);

        // Stin
        let atlas = Game.getTextureAtlas(0);

        this._shadow = new pc.Entity();
        this._shadow.addComponent(pc.eCompomentType.element, {
            type: pc.ELEMENTTYPE_IMAGE,
            texture: atlas.texture,
            width: 1,
            height: 1,
            anchor: [0.5, 0.5, 0.5, 0.5],
            pivot: [0.5, 0.5],
            batchGroupId: Gameplay.BATCH_GROUP_ID[eBatchGroup.fakeShadows]
        });
        this._shadow.element.rect = TextureAtlasUtils.getTexFrameRegion(atlas.texture, (<pc.TextureAtlasFrame>atlas.frames[eAtlas_0.shadowCircle]).rect);
        this._shadow.setLocalEulerAngles(-90, 0, 0);
        Game.app.root.addChild(this._shadow);

        // Sfx and Music
        let sfxAssetId = Game.sfxSpriteAssetId;

        SoundManager.createSoundComponent(this._entity);
        SoundManager.addSoundSlot(this._entity, eSfxSprite.bonusCollect, SfxSprites[eSfxSprite.bonusCollect], sfxAssetId);
        SoundManager.addSoundSlot(this._entity, eSfxSprite.pwuActivation, SfxSprites[eSfxSprite.pwuActivation], sfxAssetId);
        SoundManager.addSoundSlot(this._entity, eSfxSprite.fall, SfxSprites[eSfxSprite.fall], sfxAssetId);
        SoundManager.addSoundSlot(this._entity, eSfxSprite.explosion, SfxSprites[eSfxSprite.explosion], sfxAssetId);
        SoundManager.addSoundSlot(this._entity, eSfxSprite.trampoline, SfxSprites[eSfxSprite.trampoline], sfxAssetId);
        SoundManager.addSoundSlot(this._entity, eSfxSprite.tileActivation, SfxSprites[eSfxSprite.tileActivation], sfxAssetId);
        SoundManager.addSoundSlot(this._entity, eSfxSprite.platform, SfxSprites[eSfxSprite.platform], sfxAssetId);
        SoundManager.addSoundSlot(this._entity, eSfxSprite.slide, SfxSprites[eSfxSprite.slide], sfxAssetId);

        this._entity.sound.addSlot(eAudioAsset.music, {
            loop: true,
            volume: 0.6,
            asset: Game.app.assets.find(eAudioAsset.music, pc.eAssetType.audio).id
        });

        if (Game.API == eGameAPI.GAMEE)
            Gamee.Wrapper.EVENTS.on(Gamee.eEvent.unmute, () => { SoundManager.mute = false; SoundManager.playSound(this._entity, eAudioAsset.music); }, this);

        // Audio listener
        this._entity.addComponent(pc.eCompomentType.audiolistener);

        // Pozice
        this._pos = new pc.Vec3();
        this._rotation = new pc.Vec3();
        this._cameraOrigin = new pc.Vec3();
        this._platformColRc = new Rectangle();

        // Ovladani
        this._controls = new Controls(this);
        this._controls.activate();

        // Particles
        this._crashPartSys = this._entity.addComponent(pc.eCompomentType.particlesystem, {
            loop: false,
            autoPlay: false,
            lifetime: 1,
            rate: 0,
            rate2: 0,
            numParticles: 20,
            //stretch: 0.75,
            //alignToMotion: true,
            emitterShape: pc.EMITTERSHAPE_SPHERE,
            emitterRadius: 0.5,
            //emitterRadiusInner: 0.25,
            colorMap: Game.app.assets.find(eTextureAsset.particles, pc.ASSET_TEXTURE).resource,
        }) as pc.ParticleSystemComponent;

        this._crashPartSys.velocityGraph = new pc.CurveSet([
            [0, -2, 1, 0],
            [0, 0, 1, 0],
            [0, -2, 1, 0]
        ]);
        this._crashPartSys.velocityGraph.type = pc.CURVE_SMOOTHSTEP;

        this._crashPartSys.velocityGraph2 = new pc.CurveSet([
            [0, 1, 1, 0],
            [0, 2, 1, 0],
            [0, 2, 1, 0]
        ]);
        this._crashPartSys.velocityGraph2.type = pc.CURVE_SMOOTHSTEP;

        this._crashPartSys.radialSpeedGraph = this._crashPartSys.radialSpeedGraph2 = new pc.Curve([0, 1, 1, 0]);
        this._crashPartSys.radialSpeedGraph.type = this._crashPartSys.radialSpeedGraph2.type = pc.CURVE_SMOOTHSTEP;
        this._crashPartSys.scaleGraph = new pc.Curve([0, 0.05, 1, 0.2]);
        this._crashPartSys.scaleGraph.type = pc.CURVE_SMOOTHSTEP;
        this._crashPartSys.scaleGraph2 = new pc.Curve([0, 0.15, 1, 0.25]);
        this._crashPartSys.scaleGraph2.type = pc.CURVE_SMOOTHSTEP;
        this._crashPartSys.alphaGraph = this._crashPartSys.alphaGraph2 = new pc.Curve([0, 1, 1, 0]);
        this._crashPartSys.alphaGraph.type = this._crashPartSys.alphaGraph2.type = pc.CURVE_SMOOTHSTEP;

        // Kolize
        this._groundColRc = new Rectangle(0, 0, Player.GROUND_COLRC_DEF_SIZE, Player.GROUND_COLRC_DEF_SIZE);
        this._groundColCells = [undefined, undefined, undefined, undefined];
        this._colSphere = new pc.BoundingSphere(new pc.Vec3(0, 0, 0), Player.COL_RADIUS);

        // Skore
        this._score = new Score(5, Gameplay.instance.events);

        // Checkpoint
        this._checkpoint = new Checkpoint();
    }

    //-------------------------------------------------------------------------    
    //-------------------------------------------------------------------------
    reset(): void {
        // Nastavit pozici
        this._pos.set(GameWorld.CENTER_X, GameWorld.GROUND_Y + Player.GROUND_OFFSET_Y, GameWorld.Z - View.VIEW_CAM_OFFSET_Z - View.CAM_PL_OFFSET_Z);

        this.resetInt();

        // Skore
        this._score.reset();
        this._disScorePos = this._pos.z;

        // Checkpoint a zivoty
        this._checkpoint.reset();
        this._nextCheckPointPos = GameWorld.Z - PathGenerator.INSTANCE.getPathEntry(0).sectionData.rows.length * TileBase.SIZE;
        this._lifeCnt = 2;
    }

    //-------------------------------------------------------------------------
    /**
     * Restart hrace z checkpointu
     */
    //-------------------------------------------------------------------------
    restartFromCP(): void {
        // Pozice hrace podle checkpointu
        this._pos.set(GameWorld.CENTER_X, GameWorld.GROUND_Y + Player.GROUND_OFFSET_Y, this._checkpoint.pathEntry.sectionPos);
        this.resetInt();

        // Skore
        this._score.reset();
        this._score.incScore(this._checkpoint.score);

        // Pozice, na ktere doslo k posledni aktualizaci skore za ujetou vzdalenost
        this._disScorePos = Math.floor((this._pos.z - (GameWorld.Z - View.VIEW_CAM_OFFSET_Z - View.CAM_PL_OFFSET_Z)) / Player.DIS_SCORE_INTERVAL);
    }

    //-------------------------------------------------------------------------
    //--------------------------------- ----------------------------------------
    private resetInt(): void {
        // Pozice hrace
        this._cameraOrigin.copy(this._pos);
        this._entity.setPosition(this._pos);

        // Rotace
        this._rotation.set(0, 0, 0);
        this._entity.setLocalEulerAngles(this._rotation);

        // Zajistit viditelnost
        this._entity.model.show();

        // Stin
        this._shadow.setPosition(this._pos.x, GameWorld.SHADOW_Y, this._pos.z);
        this._shadow.enabled = true;

        // Kolizni sphere
        this._colSphere.center.copy(this._pos);

        // Pohyb
        this._vSpeed = Player.DEF_V_SPEED;
        this._vSpeedScale = 1;
        this._hSpeed = 0;
        this._platform = undefined;

        // Ovladani
        this._controls.reset();

        // Stav
        this._state = ePlayerState.idle;
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    update(): void {
        let fnc = this._stateProcessFnc[this._state];

        if (fnc)
            fnc.call(this);
    }

    //-------------------------------------------------------------------------
    /**
     * Zahajeni skoku
     */
    //-------------------------------------------------------------------------
    startJump(): void {
        this._jumpStartZ = this._pos.z;

        /*let i = Math.ceil(this._jumpStartZ / TileBase.SIZE);
        i *= TileBase.SIZE;
        i -= (4.2 * TileBase.SIZE);
        i = this._jumpStartZ - i;*/

        this._jumpDistance = this._jumpStartZ - ((Math.ceil(this._jumpStartZ / TileBase.SIZE) * TileBase.SIZE) - (4.2 * TileBase.SIZE));

        // Skryt stin
        this._shadow.enabled = false;

        // Zvuk
        SoundManager.playSound(this._entity, eSfxSprite.trampoline);

        // Mise
        if (Game.API == eGameAPI.GAMEE)
            Game.missions.incMissionProgress(eMisstionType.jump);

        this._state = ePlayerState.jump;
    }

    //-------------------------------------------------------------------------
    /**
     * Zabit hrace
     */
    //-------------------------------------------------------------------------
    kill(): void {
        if (this._state == ePlayerState.rolling || this._state == ePlayerState.jump) {
            this._state = ePlayerState.crashToDeath;
            this._stateTime = TIMER.time;

            SoundManager.playSound(this._entity, eSfxSprite.explosion);

            this._entity.model.hide();
            this._shadow.enabled = false;
            this._entity.particlesystem.reset();
            this._entity.particlesystem.play();

            View.instance.startShake(1, 750);

            // Log event
            this.logDeathDistance();
        }
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    private logDeathDistance(): void {
        if (Game.API == eGameAPI.GAMEE)
            Gamee.Wrapper.logEvent(eLogEvent.death, (((~~(-this._pos.z / TileBase.SIZE)) / PathGenerator.INSTANCE.rowCnt) * 100).toString());
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    private processIdle(): void {
        if (this._controls.forceDir != 0) {
            // Start music
            SoundManager.playSound(this._entity, eAudioAsset.music);

            // Zahajit pohyb kulicky
            this._state = ePlayerState.rolling;
            this.processRolling();

            // Aktualizovat HUD
            HUD.instance.setState(eHUDState.gameplay);

            // Oznamit zahajeni hry GAMEE
            if (Game.API == eGameAPI.GAMEE)
                Gamee.Wrapper.gameStart();
        }
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    private processRolling(): void {
        // Vypocitat pozici na ose X a Z
        this.calculatePositionXZ();

        // Aktualizovat pozici entity
        this._entity.setPosition(this._pos);
        this._shadow.setPosition(this._pos.x, GameWorld.SHADOW_Y, this._pos.z);

        // Aktualizovat kolizni sphere
        this._colSphere.center.copy(this._pos);

        // Testovat kolize
        this.checkGroundCollision();
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    private processJump(): void {
        // Vypocitat pozici na X a Z
        this.calculatePositionXZ();

        // Vypocitat pozici na Y
        let progress = (this._jumpStartZ - this._pos.z) / this._jumpDistance;

        if (progress > 1) {
            progress = 1;

            // Upravit pozici na ose Z, tak aby nebyla prekrocena delka skoku
            this._cameraOrigin.z = this._pos.z = this._jumpStartZ - this._jumpDistance;
        }

        if (progress < 0.5) {
            this._pos.y = GameWorld.GROUND_Y + Player.GROUND_OFFSET_Y + Player.JUMP_HEIGHT * pc.QuadraticOut(progress / 0.5);
        }
        else {
            this._pos.y = GameWorld.GROUND_Y + Player.GROUND_OFFSET_Y + Player.JUMP_HEIGHT - Player.JUMP_HEIGHT * pc.QuadraticIn((progress - 0.5) / 0.5);
        }

        // Aktualizovat pozici entity
        this._entity.setPosition(this._pos);

        // Aktualizovat kolizni sphere
        this._colSphere.center.copy(this._pos);

        if (progress == 1) {
            this._state = ePlayerState.rolling;
            this._shadow.enabled = true;
            this.checkGroundCollision();
        }
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    private processFall(): void {
        let time = TIMER.time - this._stateTime;

        // Vypocitat pozici na X a Z        
        if ((this._vSpeedScale = 1 - time / 500) < 0)
            this._vSpeedScale = 0;

        this.calculatePositionXZ();

        // Vypocitat pozici na Y
        this._pos.y -= this._fallSpeed * TIMER.deltaS

        // Aktualizovat pozici
        this._entity.setPosition(this._pos);

        // Aktualizovat rychlost padu
        this._fallSpeed += Player.FALL_SPEED_INC * TIMER.deltaS;

        if (time >= 1000)
            this.setDeathState();
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    private processCrash(): void {
        if (TIMER.time - this._stateTime > 1000)
            this.setDeathState();
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    private setDeathState(): void {
        // High score
        if (SaveState.highScore < this._score.value) {
            SaveState.highScore = this._score.value;
            this._isNewHighScore = true;
        }
        else {
            this._isNewHighScore = false;
        }

        this._lifeCnt--;
        this._state = ePlayerState.death;
        Gameplay.instance.events.fire(Player.EVENT_DEATH, this);
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    private calculatePositionXZ(): void {
        // Pohyb smerem vpred       
        let stepZ = this._vSpeed * TIMER.deltaS;

        if (this._platform) {
            // Pouzit krok, o ktery se posunula plosina
            let s = this._platform.moveStep;

            if (this._platform.state == ePlatformState.moving) {
                stepZ = s;
            }
            else {
                this._platform = undefined;

                if (s > stepZ)
                    stepZ = s;
            }
        }

        this._cameraOrigin.z -= stepZ;
        this._pos.z -= stepZ * this._vSpeedScale;

        // Pohyb vlevo/vpravo
        if (this._controls.forceDir != 0) {
            this._hSpeed += (Player.H_SPEED_ACCELERATION * TIMER.deltaS) * this._controls.forceDir;

            if (this._hSpeed > Player.MAX_H_SPEED) {
                this._hSpeed = Player.MAX_H_SPEED;
            }
            else if (this._hSpeed < -Player.MAX_H_SPEED) {
                this._hSpeed = -Player.MAX_H_SPEED;
            }
        }
        else if (this._hSpeed > 0) {
            if ((this._hSpeed -= Player.H_SPEED_DECELERATION * TIMER.deltaS) < 0)
                this._hSpeed = 0;
        }
        else if (this._hSpeed < 0 && (this._hSpeed += Player.H_SPEED_DECELERATION * TIMER.deltaS) > 0) {
            this._hSpeed = 0;
        }

        if (this._hSpeed != 0) {
            let camOriginX = (this._pos.x += this._hSpeed * TIMER.deltaS);

            if (camOriginX > Player.MAX_X) {
                camOriginX = Player.MAX_X;
            }
            else if (camOriginX < Player.MIN_X) {
                camOriginX = Player.MIN_X;
            }

            this._cameraOrigin.x = camOriginX;
        }

        // Rotace
        this._rotation.x += stepZ * -100;
        this._rotation.y = -(this._hSpeed / Player.MAX_H_SPEED) * 60;
        this._entity.setLocalEulerAngles(this._rotation);

        // Skore za ujetou vzdalenost
        if (this._pos.z <= this._disScorePos - Player.DIS_SCORE_INTERVAL) {
            let val = Math.floor((this._disScorePos - this._pos.z) / Player.DIS_SCORE_INTERVAL);

            this._score.incScore(val * Player.DIS_SCORE);
            this._disScorePos -= val * Player.DIS_SCORE_INTERVAL;
        }

        // Dosazeni checkpointu
        if (this._pos.z <= this._nextCheckPointPos) {
            // Nastavit checkpoint
            this._checkpoint.set(this._score.value, this._checkpoint.valid ? this._checkpoint.pathEntryId + 1 : 1);

            // Pozice pristiho checkpointu
            this._nextCheckPointPos -= this._checkpoint.pathEntry.sectionData.rows.length * TileBase.SIZE;
        }
    }

    //-------------------------------------------------------------------------
    /**
     * Testovani kolizi se zemi
     */
    //-------------------------------------------------------------------------
    private checkGroundCollision(): void {
        // Ziskat kolizni dlazdice z mapy view
        this._groundColRc.x = this._pos.x - this._groundColRc.width / 2;
        this._groundColRc.y = this._pos.z - this._groundColRc.height / 2;

        let ground = false;

        if (!this._platform) {
            let cellId = ViewCellMap.instance.getCollisionCells(this._groundColRc, this._groundColCells);

            if (cellId != 0) {
                while (cellId-- != 0) {
                    let cell = this._groundColCells[cellId];

                    if (cell.type == eCellType.tile) {
                        // Dlazdice
                        let tile = cell.groundTile;

                        if (tile.ground)
                            ground = true;

                        if (tile.handleCollision(this)) {
                            switch (tile.type) {
                                case eTileType.fragile: {
                                    SoundManager.playSound(this._entity, eSfxSprite.tileActivation);
                                    break;
                                }
                            }
                        }
                    }
                    else if (cell.type == eCellType.platform) {
                        // Pojizdna plosina
                        ground = true;

                        // Aktivovat plosinu (aktivuje se pouze, pokud jeste nebyla aktivovana)
                        if (cell.platform.state == ePlatformState.atStartPos) {
                            let plat = cell.platform;

                            plat.getColRect(this._platformColRc);

                            if (this._pos.z <= this._platformColRc.y + TileBase.SIZE / 1.5) {
                                if ((this._pos.x < this._platformColRc.right && this._pos.x >= this._platformColRc.x + this._groundColRc.width / 3) ||
                                    (this._pos.x > this._platformColRc.x && this._pos.x <= this._platformColRc.right - this._groundColRc.width / 3)) {
                                    this._platform = plat;
                                    this._platform.activate();

                                    SoundManager.playSound(this._entity, eSfxSprite.platform);
                                }
                            }
                        }
                    }
                }
            }
        }
        else {
            this._platform.getColRect(this._platformColRc);

            if (this._platformColRc.intersect(this._groundColRc))
                ground = true;
        }

        if (!ground) {
            // Zahajit pad
            this._state = ePlayerState.fallToDeath;
            this._fallSpeed = Player.FALL_START_SPEED;
            this._stateTime = TIMER.time;

            this._shadow.enabled = false;

            SoundManager.playSound(this._entity, eSfxSprite.fall);

            // Log event
            this.logDeathDistance();
        }
    }
}